<template>
  <div class="my-claims-page">
    <!-- ID Level 0: Service Code Required -->
    <div v-if="idLevel === 0" class="claims-content">
      <ServiceCodeRequiredCard class="mb-24" />
      <ReportClaimCard v-if="isDa()" />
    </div>

    <!-- ID Level 1: Load Claims from API -->
    <div v-else-if="idLevel === 1" class="claims-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <Spinner />
      </div>

      <!-- Error State: Service Unavailable (4xx, 5xx) -->
      <template v-else-if="hasError">
        <ServiceUnavailableCard class="mb-24" />
        <ReportClaimCard v-if="isDa()" />
      </template>

      <!-- Empty State: No Claims Found -->
      <template v-else-if="claims.length === 0">
        <NoClaimsCard class="mb-24" />
        <ReportClaimCard v-if="isDa()" />
      </template>

      <!-- Success State: Claims Found -->
      <template v-else>
        <!-- Claims List - sorted by date, newest first -->
        <div class="claims-list mb-24">
          <ClaimDataTile
            v-for="(claim, index) in sortedClaims"
            :key="claim.claimReference.claimId"
            :claim="claim"
            :claim-id="claim.claimReference.claimId"
            :claim-role="claim.claimReference.claimRole"
            :default-expanded="index === 0"
          />
        </div>

        <!-- Report New Claim Card -->
        <InfoClaimsCard class="mb-24" />
        <ReportClaimCard class="mb-24" v-if="isDa()" />
      </template>
    </div>

    <!-- ID Level Unknown/Error -->
    <div v-else class="claims-content">
      <p>{{ $t('claims.error.noIdLevel') }}</p>
    </div>
  </div>
  <div v-if="isClaimsTest">
    <CustomButton
      id="schaeden.save-button"
      :buttonColor="ButtonColor.PRIMARY"
      @click="handleEvent"
      :disabledState="false"
    >
      Claims Daten speichern
    </CustomButton>
    <div ref="claimsFeedbackDiv" data-testid="claimsFeedbackDiv">Warte</div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { getIdLevel, getPartnerProxyId, getToken } from '@/utils/KeycloakUtils'
import ServiceCodeRequiredCard from '@/components/claims/ServiceCodeRequiredCard.vue'
import ServiceUnavailableCard from '@/components/claims/ServiceUnavailableCard.vue'
import NoClaimsCard from '@/components/claims/NoClaimsCard.vue'
import ReportClaimCard from '@/componentsTenant/claims/ReportClaimCard.vue'
import ClaimDataTile from '@/components/claims/ClaimDataTile.vue'
import { ButtonColor, CustomButton, Spinner } from 'de-zurich-ui-library'
import type { Claim, ClaimsListResponse } from '@/types/Claim'
import InfoClaimsCard from '@/components/claims/InfoClaimsCard.vue'
import { getCookie, storeClaimsData } from '@/utils/utils'
import { isDa } from '@/utils/CommonUtils'
import { t } from '@/i18n'

const idLevel = ref<number | null>(null)
const loading = ref(false)
const hasError = ref(false)
const claims = ref<Claim[]>([])

// API endpoint from ENV_CONFIG
const claimsApi = globalThis.ENV_CONFIG?.SERVICE_HOSTS?.CLAIMS || ''

const sortByClaimDate = () => {
  return (
    a: { lossDate: string | number | Date },
    b: { lossDate: string | number | Date }
  ) => {
    const dateA = new Date(a.lossDate).getTime()
    const dateB = new Date(b.lossDate).getTime()
    return dateB - dateA // Descending order (newest first)
  }
}

const sortedClaims = computed(() => {
  return [...claims.value].sort(sortByClaimDate())
})

// Fetch claims list from API
const fetchClaimsList = async (): Promise<void> => {
  loading.value = true
  hasError.value = false

  const partnerProxyId = getPartnerProxyId()

  if (!partnerProxyId) {
    console.error('Missing partnerProxyId for claims fetch')
    hasError.value = true
    loading.value = false
    return
  }

  try {
    const token = await getToken()

    if (!token) {
      console.error('Missing token for claims fetch')
      hasError.value = true
      loading.value = false
      return
    }

    const companyName: string = t('common.companyName')

    const response = await fetch(
      `${claimsApi}/${partnerProxyId}?company=${companyName}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          KeyId: globalThis.ENV_CONFIG?.API_KEY,
          'Content-Type': 'application/json'
        }
      }
    )

    // Handle HTTP errors (4xx, 5xx)
    if (!response.ok) {
      console.error(`Claims API error: ${response.status} ${response.statusText}`)
      hasError.value = true
      loading.value = false
      return
    }

    const result: ClaimsListResponse = await response.json()

    // Set claims (empty array if no claims)
    claims.value = result || []
  } catch (error) {
    console.error('Error fetching claims:', error)
    hasError.value = true
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  idLevel.value = getIdLevel()

  if (idLevel.value === null) {
    console.error('No id level available!')
    return
  }

  // Only fetch claims if ID level is 1
  if (idLevel.value === 1) {
    fetchClaimsList()
  }
})

// #########################
// Only Claims Test -- START
const isClaimsTest = computed(() => {
  return getCookie('claims-test') !== null
})
const claimsFeedbackDiv = ref<HTMLDivElement | null>(null)

function handleEvent(): void {
  if (claimsFeedbackDiv.value) {
    claimsFeedbackDiv.value.textContent = 'Daten speichern ...'
  }
  storeClaimsData(isDa() ? 'DA' : 'ZUR')
    .then(() => {
      if (claimsFeedbackDiv.value) {
        claimsFeedbackDiv.value.textContent = 'Daten wurden gespeichert.'
      }
    })
    .catch((error) => {
      if (claimsFeedbackDiv.value) {
        claimsFeedbackDiv.value.textContent = 'Fehler beim speichern: ' + error
      }
    })
}

addEventListener('storeClaimsData', handleEvent)
// Only Claims Test -- END
// #########################
</script>

<style scoped lang="scss">
.my-claims-page {
  max-width: 100%;
}

.claims-content {
  max-width: 1200px;
  margin: 0 auto;
}

.claims-list {
  display: flex;
  flex-direction: column;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}
</style>
